import React, { forwardRef } from 'react';
import { Button, Select, TextInput, MultiSelect, Group, ActionIcon, Tooltip } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconPlus, IconTrash } from '@tabler/icons';
import type { 
  ActionProps, 
  ValueSelectorProps, 
  ValueEditorProps,
  Option,
  FieldSelectorProps,
  OperatorSelectorProps,
  Field,
  Operator,
  CombinatorSelectorProps,
  NotToggleProps,
  Combinator
} from 'react-querybuilder';
import { 
  OPERATOR_CATEGORIES, 
  getOperatorCategory, 
  shouldShowValueInput, 
  getPlaceholderText,
  getFieldType,
  getInputComponentType,
  shouldUseMultiSelect
} from '../operators';

interface SelectOption {
  value: string;
  label: string;
}

// Custom item component for Mantine Select/MultiSelect to show both label and value
const SelectItem = forwardRef(
  (
    { label, value, ...others }: { label: string; value: string },
    ref: React.Ref<HTMLDivElement>
  ) => (
    <div ref={ref} {...others} style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 11, color: '#888' }}>{value}</span>
    </div>
  )
);

// Custom Action Button (Add/Remove/etc)
const ActionElement: React.FC<ActionProps> = ({ label, className, handleOnClick: onClick, disabled }) => {
  // Style the button based on the label
  const getButtonProps = () => {
    if (label?.includes('Remove')) {
      return {
        variant: 'subtle' as const,
        color: 'red' as const,
        compact: true,
      };
    }
    if (label?.includes('Add Rule')) {
      return {
        variant: 'light' as const,
        color: 'blue' as const,
        compact: true,
      };
    }
    if (label?.includes('Add Group')) {
      return {
        variant: 'outline' as const,
        color: 'blue' as const,
        compact: true,
      };
    }
    return {
      variant: 'subtle' as const,
      color: 'gray' as const,
      compact: true,
    };
  };

  const buttonProps = getButtonProps();
  const buttonText = label?.replace('Add ', '+').replace('Remove', '×') || '';

  return (
    <Button
      {...buttonProps}
      size="xs"
      onClick={onClick}
      disabled={disabled}
      className={className}
      styles={(theme) => ({
        root: {
          height: 24,
          padding: '0 8px',
          fontSize: theme.fontSizes.xs,
          fontWeight: buttonText === '×' ? 'bold' : 'normal',
        },
      })}
    >
      {buttonText}
    </Button>
  );
};

// Custom Combinator Selector
const CombinatorSelector: React.FC<CombinatorSelectorProps> = ({
  value,
  options = [],
  className,
  handleOnChange,
  disabled,
}) => {
  const [customOptions, setCustomOptions] = React.useState<SelectOption[]>([]);
  
  // Add current value to options if it exists and isn't already in the options
  React.useEffect(() => {
    if (value && !options.some(opt => 
      ('name' in opt && String(opt.name) === value) || 
      (typeof opt === 'string' && opt === value)
    )) {
      setCustomOptions(prev => {
        // Check if it's already in custom options
        if (!prev.some(opt => opt.value === value)) {
          return [...prev, { value: String(value), label: String(value) }];
        }
        return prev;
      });
    }
  }, [value, options]);
  
  const combinatorOptions = [...options.map(option => {
    if ('name' in option) {
      return {
        value: String(option.name),
        label: String(option.label),
      };
    }
    return null;
  })
    .filter((option): option is SelectOption => option !== null), ...customOptions];

  return (
    <Group spacing="xs" className={className}>
      <Select
        value={value}
        onChange={handleOnChange}
        data={combinatorOptions}
        disabled={disabled}
        withinPortal
        label="Operator"
        size="xs"
        clearable={false}
        w={90}
        searchable
        creatable
        getCreateLabel={(query) => `+ Create "${query}"`}
        onCreate={(query) => {
          const item = { value: query, label: query };
          setCustomOptions(prev => [...prev, item]);
          handleOnChange(query);
          return item;
        }}
        itemComponent={SelectItem}
        styles={{
          root: {
            minWidth: 50,
          },
          input: {
            color: '#4c6ef5',
            fontWeight: 500,
            height: 24,
            minHeight: 24,
            lineHeight: '24px',
            backgroundColor: '#edf2ff',
            borderColor: '#edf2ff',
          },
        }}
      />
    </Group>
  );
};

// Custom Field Selector
const FieldSelector: React.FC<FieldSelectorProps> = ({
  options = [],
  value,
  title,
  className,
  handleOnChange,
  disabled,
}) => {
  const [customOptions, setCustomOptions] = React.useState<SelectOption[]>([]);
  
  // Add current value to options if it exists and isn't already in the options
  React.useEffect(() => {
    if (value && !options.some(opt => 
      ('name' in opt && String(opt.name) === value) || 
      (typeof opt === 'string' && opt === value)
    )) {
      setCustomOptions(prev => {
        // Check if it's already in custom options
        if (!prev.some(opt => opt.value === value)) {
          return [...prev, { value: String(value), label: String(value) }];
        }
        return prev;
      });
    }
  }, [value, options]);
  
  const selectOptions = [...options.map(option => {
    if ('name' in option) {
      return {
        value: String(option.name),
        label: String(option.label),
      };
    }
    // Handle option groups if needed
    return {
      value: '',
      label: '',
    };
  }).filter(option => option.value !== ''), ...customOptions] as SelectOption[];

  return (
    <Select
      value={value}
      onChange={handleOnChange}
      data={selectOptions}
      placeholder="Select field"
      disabled={disabled}
      size="xs"
      className={className}
      clearable={false}
      withinPortal
      searchable
      creatable
      getCreateLabel={(query) => `+ Create "${query}"`}
      onCreate={(query) => {
        const item = { value: query, label: query };
        setCustomOptions(prev => [...prev, item]);
        handleOnChange(query);
        return item;
      }}
      itemComponent={SelectItem}
      styles={{
        input: {
          borderRadius: '4px 0 0 4px',
        }
      }}
    />
  );
};

// Custom Operator Selector
const OperatorSelector: React.FC<OperatorSelectorProps> = ({
  options = [],
  value,
  title,
  className,
  handleOnChange,
  disabled,
}) => {
  const [customOptions, setCustomOptions] = React.useState<SelectOption[]>([]);
  
  // Add current value to options if it exists and isn't already in the options
  React.useEffect(() => {
    if (value && !options.some(opt => 
      ('name' in opt && String(opt.name) === value) || 
      (typeof opt === 'string' && opt === value)
    )) {
      setCustomOptions(prev => {
        // Check if it's already in custom options
        if (!prev.some(opt => opt.value === value)) {
          return [...prev, { value: String(value), label: String(value) }];
        }
        return prev;
      });
    }
  }, [value, options]);
  
  const selectOptions = [...options.map(option => {
    if ('name' in option) {
      return {
        value: String(option.name),
        label: String(option.label),
      };
    }
    // Handle option groups if needed
    return {
      value: '',
      label: '',
    };
  }).filter(option => option.value !== ''), ...customOptions] as SelectOption[];

  return (
    <Select
      value={value}
      onChange={handleOnChange}
      data={selectOptions}
      placeholder="Select operator"
      disabled={disabled}
      size="xs"
      className={className}
      clearable={false}
      withinPortal
      searchable
      creatable
      getCreateLabel={(query) => `+ Create "${query}"`}
      onCreate={(query) => {
        const item = { value: query, label: query };
        setCustomOptions(prev => [...prev, item]);
        handleOnChange(query);
        return item;
      }}
      itemComponent={SelectItem}
      styles={{
        input: {
          color: '#4c6ef5',
          backgroundColor: '#edf2ff',
          borderColor: '#edf2ff',
          borderTopColor: '#ced4da',
          borderBottomColor: '#ced4da',
          borderRadius: '0',
        }
      }}
    />
  );
};

// Custom Value Selector
const ValueSelector: React.FC<ValueSelectorProps> = ({
  value,
  options = [],
  multiple,
  disabled,
  className,
  handleOnChange,
  path,
  level,
  schema,
}) => {
  const [customOptions, setCustomOptions] = React.useState<SelectOption[]>([]);
  
  // Add current value to options if it exists and isn't already in the options
  React.useEffect(() => {
    const checkAndAddValue = (val: any) => {
      if (val && !options.some(opt => 
        (typeof opt === 'string' && opt === val) || 
        ('name' in opt && String(opt.name) === val) ||
        ('value' in opt && String(opt.value) === val)
      )) {
        setCustomOptions(prev => {
          // Check if it's already in custom options
          if (!prev.some(opt => opt.value === val)) {
            return [...prev, { value: String(val), label: String(val) }];
          }
          return prev;
        });
      }
    };
    
    if (multiple && Array.isArray(value)) {
      value.forEach(checkAndAddValue);
    } else if (value) {
      checkAndAddValue(value);
    }
  }, [value, options, multiple]);
  
  const selectOptions = [...options.map(option => {
    if (typeof option === 'string') {
      return { value: String(option), label: String(option) };
    }
    if ('name' in option) {
      return { value: String(option.name), label: String(option.label) };
    }
    if ('value' in option) {
      return { 
        value: String(option.value), 
        label: option.label ? String(option.label) : String(option.value) 
      };
    }
    return { value: '', label: '' };
  }).filter(option => option.value !== ''), ...customOptions] as SelectOption[];

  if (multiple) {
    const arrayValue = Array.isArray(value) ? value : value ? [value] : [];
    return (
      <MultiSelect
        value={arrayValue.map(String)}
        onChange={handleOnChange}
        data={selectOptions}
        disabled={disabled}
        size="xs"
        className={className}
        clearable
        placeholder="Select or create values"
        searchable
        creatable
        getCreateLabel={(query) => `+ Create "${query}"`}
        onCreate={(query) => {
          const item = { value: query, label: query };
          setCustomOptions(prev => [...prev, item]);
          handleOnChange([...(arrayValue || []), query]);
          return item;
        }}
        itemComponent={SelectItem}
        styles={{
          root: {
            backgroundColor: '#edf2ff',
          },
          input: {
            backgroundColor: '#edf2ff',
            borderRadius: '0 4px 4px 0',
          }
        }}
      />
    );
  }

  return (
    <Select
      value={value as string}
      onChange={handleOnChange}
      data={selectOptions}
      disabled={disabled}
      size="xs"
      className={className}
      clearable
      placeholder="Select or create value"
      searchable
      creatable
      withinPortal
      getCreateLabel={(query) => `+ Create "${query}"`}
      onCreate={(query) => {
        const item = { value: query, label: query };
        setCustomOptions(prev => [...prev, item]);
        handleOnChange(query);
        return item;
      }}
      itemComponent={SelectItem}
      styles={{
        input: {
          backgroundColor: '#edf2ff',
          borderRadius: '0 4px 4px 0',
        }
      }}
    />
  );
};

// Custom Value Editor
const ValueEditor: React.FC<ValueEditorProps> = ({
  value,
  handleOnChange,
  type,
  inputType,
  values = [],
  disabled,
  className,
  path,
  level,
  schema,
  operator,
  field,
}) => {
  const [customOptions, setCustomOptions] = React.useState<SelectOption[]>([]);
  
  // Determine field type and input component type
  const fieldType = getFieldType(field || '', inputType || '', schema);
  const hasValues = values.length > 0;
  const componentType = getInputComponentType(operator || '', fieldType, hasValues);
  const placeholder = getPlaceholderText(operator || '', field);
  
  // Don't render any input for operators that don't need values
  if (componentType === 'none') {
    return null;
  }
  
  // Render Boolean Input (for 'exists' operator)
  const renderBooleanInput = () => (
    <Select
      value={value ? 'true' : 'false'}
      onChange={(val) => handleOnChange(val === 'true')}
      data={[
        { value: 'true', label: 'True' },
        { value: 'false', label: 'False' }
      ]}
      disabled={disabled}
      size="xs"
      className={className}
      clearable={false}
      placeholder={placeholder}
      styles={{
        input: {
          borderRadius: '0 4px 4px 0',
        }
      }}
    />
  );

  // Render Number Input
  const renderNumberInput = () => (
    <TextInput
      value={value || ''}
      onChange={(e) => {
        const numValue = e.currentTarget.value;
        // For hasLength operator, parse as integer; for others, keep as string for backend
        if (operator === 'hasLength') {
          handleOnChange(numValue === '' ? null : parseInt(numValue, 10));
        } else {
          handleOnChange(numValue === '' ? null : numValue);
        }
      }}
      type="number"
      disabled={disabled}
      size="xs"
      className={className}
      placeholder={placeholder}
      min={operator === 'hasLength' ? 0 : undefined}
      styles={{
        input: {
          borderRadius: '0 4px 4px 0',
        }
      }}
    />
  );

  // Render Date Input
  const renderDateInput = () => (
    <DateInput
      value={value ? new Date(value) : null}
      onChange={(date: Date | null) => handleOnChange(date?.toISOString() || null)}
      disabled={disabled}
      size="xs"
      className={className}
      placeholder={placeholder}
      clearable
      styles={{
        input: {
          borderRadius: '0 4px 4px 0',
        }
      }}
    />
  );

  // Render Date Range Input
  const renderDateRangeInput = () => {
    const [startDate, endDate] = Array.isArray(value) ? value : [null, null];
    return (
      <div className={className} style={{ display: 'flex', gap: '8px' }}>
        <DateInput
          value={startDate ? new Date(startDate) : null}
          onChange={(date: Date | null) => {
            const endVal = Array.isArray(value) ? value[1] : null;
            handleOnChange([date?.toISOString() || null, endVal]);
          }}
          disabled={disabled}
          size="xs"
          placeholder="Start date"
          clearable
        />
        <DateInput
          value={endDate ? new Date(endDate) : null}
          onChange={(date: Date | null) => {
            const startVal = Array.isArray(value) ? value[0] : null;
            handleOnChange([startVal, date?.toISOString() || null]);
          }}
          disabled={disabled}
          size="xs"
          placeholder="End date"
          clearable
        />
      </div>
    );
  };

  // Render MultiSelect Input
  const renderMultiSelectInput = () => {
    const formattedValues = hasValues ? 
      [...values.map(option => {
        if (typeof option === 'string') {
          return { value: String(option), label: String(option) };
        }
        if ('name' in option) {
          return { value: String(option.name), label: String(option.label) };
        }
        if ('value' in option) {
          return { 
            value: String(option.value), 
            label: option.label ? String(option.label) : String(option.value) 
          };
        }
        return { value: '', label: '' };
      }).filter(option => option.value !== ''), ...customOptions] as SelectOption[] :
      customOptions;

    const arrayValue = Array.isArray(value) ? value : value ? [value] : [];
    
    return (
      <MultiSelect
        value={arrayValue.map(String)}
        onChange={handleOnChange}
        data={formattedValues}
        disabled={disabled}
        size="xs"
        className={className}
        clearable
        placeholder={placeholder}
        searchable
        creatable
        getCreateLabel={(query) => `+ Create "${query}"`}
        onCreate={(query) => {
          const item = { value: query, label: query };
          setCustomOptions(prev => [...prev, item]);
          handleOnChange([...(arrayValue || []), query]);
          return item;
        }}
        itemComponent={SelectItem}
        styles={{
          input: {
            borderRadius: '0 4px 4px 0',
            height: 24,
            overflow: 'scroll',
          }
        }}
      />
    );
  };

  // Render Select Input
  const renderSelectInput = () => {
    const formattedValues = [...values.map(option => {
      if (typeof option === 'string') {
        return { value: String(option), label: String(option) };
      }
      if ('name' in option) {
        return { value: String(option.name), label: String(option.label) };
      }
      if ('value' in option) {
        return { 
          value: String(option.value), 
          label: option.label ? String(option.label) : String(option.value) 
        };
      }
      return { value: '', label: '' };
    }).filter(option => option.value !== ''), ...customOptions] as SelectOption[];

    return (
      <Select
        value={String(value || '')}
        onChange={handleOnChange}
        data={formattedValues}
        disabled={disabled}
        size="xs"
        className={className}
        clearable
        placeholder={placeholder}
        searchable
        creatable
        getCreateLabel={(query) => `+ Create "${query}"`}
        onCreate={(query) => {
          const item = { value: query, label: query };
          setCustomOptions(prev => [...prev, item]);
          handleOnChange(query);
          return item;
        }}
        itemComponent={SelectItem}
        styles={{
          input: {
            borderRadius: '0 4px 4px 0',
          }
        }}
      />
    );
  };

  // Render Text Input
  const renderTextInput = () => {
    const textInputType = fieldType === 'email' ? 'email' : 
      fieldType === 'url' ? 'url' : 
        inputType || 'text';
    
    return (
      <TextInput
        value={value || ''}
        onChange={(e) => handleOnChange(e.currentTarget.value)}
        type={textInputType}
        disabled={disabled}
        size="xs"
        className={className}
        placeholder={placeholder}
        styles={{
          input: {
            borderRadius: '0 4px 4px 0',
          }
        }}
      />
    );
  };

  // Main render logic based on component type
  switch (componentType) {
  case 'boolean':
    return renderBooleanInput();
  case 'number':
    return renderNumberInput();
  case 'date':
    return renderDateInput();
  case 'dateRange':
    return renderDateRangeInput();
  case 'multiSelect':
    return renderMultiSelectInput();
  case 'select':
    return renderSelectInput();
  case 'text':
  default:
    return renderTextInput();
  }
};

// Custom Add Rule Button
const AddRuleAction: React.FC<ActionProps> = ({ handleOnClick: onClick, disabled }) => {
  return (
    <Button
      variant="light"
      color="indigo"
      size="xs"
      onClick={onClick}
      disabled={disabled}
      styles={(theme) => ({
        root: {
          height: 24,
          padding: '0 8px',
          fontSize: theme.fontSizes.xs,
        },
      })}
    >
      + Add Rule
    </Button>
  );
};

// Custom Remove Rule Button
const RemoveRuleAction: React.FC<ActionProps> = ({ handleOnClick: onClick, disabled }) => {
  return (
    <Tooltip label="Remove Rule" withArrow>
      <ActionIcon
        variant="light"
        color="red"
        ml={6}
        size={30}
        onClick={onClick}
        disabled={disabled}
        styles={(theme) => ({
          root: {
            '&:hover': {
              backgroundColor: theme.fn.rgba(theme.colors.red[2], 0.35),
            },
          },
        })}
      >
      ×
      </ActionIcon>
    </Tooltip>
  );
};

const NullComponent = () => null;

export const MantineQueryBuilderComponents = {
  actionElement: ActionElement,
  combinatorSelector: CombinatorSelector,
  fieldSelector: FieldSelector,
  operatorSelector: OperatorSelector,
  valueEditor: ValueEditor,
  valueSelector: ValueSelector,
  addRuleAction: NullComponent,
  removeRuleAction: RemoveRuleAction,
  addGroupAction: NullComponent,
}; 